import Cookie from 'js-cookie'
import Events from '../events'
import EventBus from '../event-bus'
import logger from '../../logger'
import { COBRAND_QUERY_NAME } from '@/lib/constants/cobrands.js'
import { LogoTracker, LTEvents } from '~/lib/events/tracker'
import { ENGLISH_COUNTRY_CODES } from '~/lib/constants/countries'

function identifyCIOUser(user) {
  const rawCode = user.country || Cookie.get('country') || ''

  const detectedCountryCode = rawCode.toLowerCase()
  const isEnglishCountry = ENGLISH_COUNTRY_CODES.includes(detectedCountryCode)

  logger.log(
    `Detected country code: ${detectedCountryCode} - Can track: ${isEnglishCountry}.  CIO loaded: ${!!window._cio}`
  )

  if (isEnglishCountry && window._cio) {
    const payload = {
      id: user.id,
      email: user.email,
      plan_level: user.plan.package.code,
      created_at: Math.round(new Date(user.date_created).getTime() / 1000),
    }

    logger.log('Identifying CIO user', payload)
    window._cio.identify(payload)
  }
}

function identifyBeaconUser(user) {
  if (typeof Beacon === 'function') {
    Beacon('identify', {
      id: user.id,
      name: `${user.given_name} ${user.family_name}`.trim(),
      given_name: user.given_name,
      family_name: user.family_name,
      email: user.email,
      plan_level: user.plan.package.code,
    })
  }
}

function addIdentifyBeaconUserEvent(user) {
  if (typeof Beacon === 'function') {
    // Unsubscribe from old events
    Beacon('off', 'open')
    // Add new event
    Beacon('on', 'open', () => {
      identifyBeaconUser(user)
    })
  }
}

EventBus.$on(
  Events.UserSignedIn,
  ({ user, method, track_analytics = true }) => {
    logger.debug('Handling UserSignedIn', user)

    LogoTracker.identify(user.id)

    LogoTracker.registerGlobalProperties({
      plan_level: user.plan.package.code,
    })

    if (track_analytics) {
      LogoTracker.track(LTEvents.LOGIN, {
        method,
      })

      EventBus.$emit(Events.AnalyticsGA4Event, {
        event: 'login',
        userId: user.id,
        method,
      })
    }

    window.$nuxt?.$sentry?.setUser({ id: user.id, email: user.email })

    identifyBeaconUser(user)
    addIdentifyBeaconUserEvent(user)

    const browserCurrency = Cookie.get('currency')

    if (browserCurrency !== user.currency) {
      Cookie.set('currency', user.currency)
      EventBus.$emit(Events.CurrencyChange, user.currency)
    }

    const updateUserPayload = {}

    if (user.country !== Cookie.get('country')) {
      updateUserPayload.country = Cookie.get('country')
    }

    window.$nuxt.$api
      .patch(`/api/v3/users/me`, {
        ...updateUserPayload,
        set_last_login: true,
      })
      .catch(e => logger.error(e))

    identifyCIOUser(user)

    Cookie.remove('unauthed_cart')
  }
)

EventBus.$on(Events.UserRegistered, async ({ user, method }) => {
  logger.debug('Handling UserRegistered', user)

  LogoTracker.identify(user.id)

  trySettingUpUser().catch(e => logger.error(e))

  EventBus.$emit(Events.AnalyticsGA4Event, {
    event: 'sign_up',
    userId: user.id,
    method,
  })

  LogoTracker.registerGlobalProperties({
    plan_level: user.plan.package.code,
  })

  LogoTracker.track(LTEvents.SIGNUP, {
    method,
    email: user.email,
  })

  window.$nuxt?.$sentry?.setUser({
    id: user.id,
    email: user.email,
  })

  identifyBeaconUser(user)
  addIdentifyBeaconUserEvent(user)

  identifyCIOUser(user)

  Cookie.remove('unauthed_cart')
})

EventBus.$on(Events.UserUpdatedAttributes, ({ user }) => {
  logger.debug('Handling UserUpdatedAttributes', user)
  Cookie.remove('unauthed_cart')

  if (!user.given_name && !user.family_name) {
    return
  }

  window.$nuxt?.$sentry?.setUser({ id: user.id, email: user.email })
})

EventBus.$on(Events.UserSignedOut, () => {
  logger.debug('Handling UserSignedOut')

  window.$nuxt?.$sentry?.setUser(null)

  LogoTracker.track(LTEvents.LOGOUT)
  LogoTracker.reset()
})

async function trySettingUpUser(retryCount = 0) {
  logger.debug('Handling UserSignup UTM params')

  if (retryCount > 30) {
    throw new Error('Failed to set up user')
  } else {
    retryCount++
  }

  await new Promise(resolve => setTimeout(resolve, 1000))

  const api = window.$nuxt.$api

  if (!api) return

  const utmParams = {
    utm_source: Cookie.get('utm_source') || 'empty',
    utm_medium: Cookie.get('utm_medium') || '(not set)',
    utm_campaign: Cookie.get('utm_campaign') || undefined,
    currency: Cookie.get('currency') || null,
    language: Cookie.get('language') || undefined,
    cobrand: Cookie.get(COBRAND_QUERY_NAME) || undefined,
    country: Cookie.get('country') || undefined,
    set_last_login: true,
  }

  if (Object.keys(utmParams).length === 0) return

  // Note that we're going to wait so that we're
  // sure the user exists fully in the backend before trying this.
  try {
    await api.patch(`/api/v3/users/me`, utmParams)
  } catch (e) {
    if (!e.response) throw e
    if (e.response.status === 404) {
      return trySettingUpUser(retryCount)
    }
  }
}
